import { useCallback, useState } from 'react';

import { useRenameAvMutation } from '../api/useRenameAvMutation';
import { hasInvalidDisplayNameCharacters } from '../utils/displayNameValidation';

interface UseRenameAnalyticalVariableDialogArgs {
  analyticalVariableId: string;
  currentName: string;
  onClose: () => void;
  /** Project this rename was triggered from, when known — threaded to
   * useRenameAvMutation so it can optimistically patch the project
   * artifact list. Omit for the org-wide AV list page. */
  projectId?: string;
}

export interface UseRenameAnalyticalVariableDialogResult {
  name: string;
  onChangeName: (id: string, value: string) => void;
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
  hasInvalidCharacters: boolean;
}

/**
 * Behaviour for the rename-analytical-variable dialog (OR-6090): name entry
 * pre-filled with the current display name, client-side validation
 * (non-empty, changed, no disallowed characters — a QoL mirror of the
 * backend's hard restriction; see `displayNameValidation.ts`), and the
 * rename mutation. No duplicate-name check — display-name uniqueness isn't
 * enforced anywhere in the system today (see the OR-6090 implementation
 * plan), so rename doesn't add one either. Intl-free so the view owns copy.
 * Shared by all three rename surfaces (AV list page, project artifact list,
 * variable detail page).
 */
export const useRenameAnalyticalVariableDialog = ({
  analyticalVariableId,
  currentName,
  onClose,
  projectId,
}: UseRenameAnalyticalVariableDialogArgs): UseRenameAnalyticalVariableDialogResult => {
  const [name, setName] = useState(currentName);
  const { mutateAsync, isPending, error, reset } = useRenameAvMutation();

  const onChangeName = useCallback((_id: string, value: string) => {
    setName(value);
  }, []);

  const trimmed = name.trim();
  const hasInvalidCharacters = hasInvalidDisplayNameCharacters(trimmed);

  const onSubmit = useCallback(async () => {
    const next = name.trim();
    if (!next || next === currentName || hasInvalidDisplayNameCharacters(next)) return;
    // Let a rejection propagate so the dialog shows the error instead of closing.
    await mutateAsync({ analyticalVariableId, dataElement: next, projectId });
  }, [analyticalVariableId, name, currentName, mutateAsync, projectId]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit: Boolean(trimmed) && trimmed !== currentName && !hasInvalidCharacters,
    hasInvalidCharacters,
  };
};
